import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Crosshair, Loader2, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import { apiFetch, useAuthStore } from '@/stores/authStore';
import { useMarketStore } from '@/stores/marketStore';
import { describeMarket } from '@/lib/kalshiTicker';
import { reconstructOrderTraderSide } from '@/lib/kalshiApi/orderReadV2';
import { computeHeadroom, formatHeadroomDisplay } from '@/lib/limits';
import type { TraderRules } from '@/types/shared/admin';
import { EditLimitsModal } from '../Manager/EditLimitsModal';

interface TraderDetailProps {
  traderId: string;
  username?: string;
  orgId?: string;
  onBack: () => void;
}

interface RawPosition {
  ticker?: string;
  market_ticker?: string;
  netCount?: number;
  position?: number;
  quantity?: number;
  side?: 'yes' | 'no';
  avgEntryPriceCents?: number;
  average_price?: number;
  realizedPnlCents?: number;
}

interface RawOrder {
  order_id?: string;
  id?: string;
  ticker?: string;
  market_ticker?: string;
  side?: 'yes' | 'no';
  status?: string;
  type?: string;
  count?: number | string;
  count_fp?: number | string;
  quantity?: number | string;
  remaining_count?: number | string;
  remaining_count_fp?: number | string;
  remaining_quantity?: number | string;
  yes_price?: number | string;
  no_price?: number | string;
  yes_price_dollars?: number | string;
  no_price_dollars?: number | string;
  price?: number | string;
}

interface RawFill {
  fill_id?: string;
  trade_id?: string;
  ticker?: string;
  market_ticker?: string;
  side?: 'yes' | 'no';
  count?: number | string;
  count_fp?: number | string;
  fill_count_fp?: number | string;
  quantity?: number | string;
  yes_price?: number | string;
  no_price?: number | string;
  yes_price_dollars?: number | string;
  no_price_dollars?: number | string;
  price?: number | string;
  price_dollars?: number | string;
  created_time?: string;
  created_at?: string;
}

function pickNum(...c: unknown[]): number | undefined {
  for (const v of c) {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v !== '') {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return undefined;
}

interface FillPnl {
  unrealizedCents: number | null;
  realizedCents: number | null;
}

/**
 * Walks fills oldest→newest per (ticker, side), maintaining the same running
 * (netCount, costBasis) state the relay's positions service uses. For each
 * fill we attach:
 *   - unrealizedCents: only set on BUY fills, marks the contracts in this
 *     fill to current market price.
 *   - realizedCents: only set on SELL fills, P&L vs running avg cost at the
 *     moment of the sell.
 * Returned as a map keyed by the original (DESC) index so render order can
 * stay newest-first.
 */
function annotateFillsWithPnl(
  fills: RawFill[],
  getMarketPrice: (ticker: string) => number | null,
  openKeys: ReadonlySet<string>,
  settlements?: Record<string, SettlementResult>,
): Map<number, FillPnl> {
  const indexed = fills.map((raw, idx) => ({ raw, idx }));
  indexed.sort((a, b) => {
    const ta = new Date(a.raw.created_time ?? a.raw.created_at ?? 0).getTime();
    const tb = new Date(b.raw.created_time ?? b.raw.created_at ?? 0).getTime();
    return ta - tb;
  });

  const stateByKey = new Map<string, { netCount: number; costBasisCents: number }>();
  const out = new Map<number, FillPnl>();
  const idxToKey = new Map<number, string>();

  for (const { raw, idx } of indexed) {
    const ticker = raw.ticker ?? raw.market_ticker ?? '';
    const side: 'yes' | 'no' = raw.side ?? 'yes';
    const action = ((raw as { action?: string }).action ?? 'buy') as 'buy' | 'sell';
    const count = pickNum(raw.count, raw.count_fp, raw.fill_count_fp, raw.quantity) ?? 0;
    const sideDollars = side === 'yes' ? raw.yes_price_dollars : raw.no_price_dollars;
    const sideCents = side === 'yes' ? raw.yes_price : raw.no_price;
    const price = dollarsToCents(sideDollars ?? raw.price_dollars, sideCents ?? raw.price) ?? 0;

    const key = `${ticker}|${side}`;
    const state = stateByKey.get(key) ?? { netCount: 0, costBasisCents: 0 };

    let unrealizedCents: number | null = null;
    let realizedCents: number | null = null;

    if (action === 'buy') {
      const winner = settlements?.[ticker];
      if (winner != null && count > 0) {
        // Market is settled — credit this BUY fill directly. For void
        // markets cost basis is refunded, so realized = 0.
        const payout = winner === 'void' ? price : winner === side ? 100 : 0;
        realizedCents = Math.round((payout - price) * count);
      } else {
        const yesPrice = getMarketPrice(ticker);
        if (yesPrice != null && count > 0) {
          const current = side === 'yes' ? yesPrice : 100 - yesPrice;
          unrealizedCents = (side === 'yes' ? current - price : price - current) * count;
        }
      }
      state.netCount += count;
      state.costBasisCents += count * price;
    } else if (action === 'sell') {
      const closing = Math.min(state.netCount, count);
      if (closing > 0) {
        const avgCost = state.costBasisCents / state.netCount;
        const perContract = side === 'yes' ? price - avgCost : avgCost - price;
        realizedCents = Math.round(perContract * closing);
        state.costBasisCents -= Math.round(avgCost * closing);
        state.netCount -= closing;
      }
    }

    stateByKey.set(key, state);
    idxToKey.set(idx, key);
    out.set(idx, { unrealizedCents, realizedCents });
  }

  // Settled-position rewrite: a (ticker, side) with positive remaining net
  // that no longer appears in the live open-positions list closed via
  // settlement (Kalshi /markets `result`), not via a SELL fill. The first
  // pass stamped its BUYs as unrealized; promote those to realized so the
  // column reflects what actually happened. For partial-sell-then-settled
  // positions this approximates (uses BUY-time stamped value rather than
  // re-deriving against the post-sell average), which is close enough.
  for (const [key, state] of stateByKey) {
    if (state.netCount > 0 && !openKeys.has(key)) {
      for (const [idx, k] of idxToKey) {
        if (k !== key) continue;
        const pnl = out.get(idx);
        if (pnl?.unrealizedCents != null) {
          out.set(idx, { unrealizedCents: null, realizedCents: pnl.unrealizedCents });
        }
      }
    }
  }

  return out;
}

function dollarsToCents(dollars: unknown, fallbackCents?: unknown): number | null {
  if (typeof dollars === 'string' && dollars !== '') {
    const n = parseFloat(dollars);
    if (!Number.isNaN(n)) return Math.round(n * 100);
  }
  if (typeof dollars === 'number') {
    return dollars <= 1.01 ? Math.round(dollars * 100) : Math.round(dollars);
  }
  if (typeof fallbackCents === 'number') return Math.round(fallbackCents);
  if (typeof fallbackCents === 'string' && fallbackCents !== '') {
    const n = parseFloat(fallbackCents);
    if (!Number.isNaN(n)) return Math.round(n);
  }
  return null;
}

type SettlementResult = 'yes' | 'no' | 'void';

interface PnlSummary {
  realizedPnlCents?: number;
  openCount?: number;
  openNotionalCents?: number;
  settlements?: Record<string, SettlementResult>;
}


function formatDollars(cents: number): string {
  const abs = Math.abs(cents);
  const sign = cents >= 0 ? '+' : '-';
  return `${sign}$${(abs / 100).toFixed(2)}`;
}

function toArr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

interface TraderLimitsSnapshot {
  id: string;
  username: string;
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules: TraderRules | null;
  openContracts: number;
  openPositions: number;
  intentContracts: number;
}

export function TraderDetail({ traderId, username, orgId, onBack }: TraderDetailProps) {
  const orgQueryParam = orgId ? `?orgId=${orgId}` : '';
  const callerRole = useAuthStore((s) => s.user?.role);
  const canEditLimits = callerRole === 'manager' || callerRole === 'admin';

  const [balance, setBalance] = useState<Record<string, unknown> | null>(null);
  const [balanceIsOrgPlaceholder, setBalanceIsOrgPlaceholder] = useState(false);
  const [positions, setPositions] = useState<RawPosition[]>([]);
  const [orders, setOrders] = useState<RawOrder[]>([]);
  const [fills, setFills] = useState<RawFill[]>([]);
  const [pnl, setPnl] = useState<PnlSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<TraderLimitsSnapshot | null>(null);
  const [editingLimits, setEditingLimits] = useState(false);

  // Subscribe to hydra-driven price ticks. Re-renders when prices change so
  // the Current / P&L columns stay live without polling Kalshi.
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  void marketVersion;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const safeFetch = async (url: string) => {
      try {
        const res = await apiFetch(url);
        const data = await res.json().catch(() => null);
        return { ok: res.ok, data };
      } catch {
        return { ok: false, data: null };
      }
    };

    // The org-rollup fetch is shared across the Manager grid and TraderDetail
    // so we don't add a per-trader limits endpoint just for this tile — the
    // rollup already carries every member's caps + usage. Filter to this
    // trader's row client-side.
    const [balRes, posRes, ordRes, fillRes, pnlRes, rollupRes] = await Promise.all([
      safeFetch(`/api/manager/traders/${traderId}/balance${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/positions${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/orders${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/fills${orgQueryParam}`),
      safeFetch(`/api/manager/traders/${traderId}/pnl${orgQueryParam}`),
      safeFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`),
    ]);

    if (rollupRes.ok && rollupRes.data) {
      const rows = (rollupRes.data as { rows?: Array<Record<string, unknown>> }).rows ?? [];
      const row = rows.find((r) => r.userId === traderId);
      if (row) {
        setLimits({
          id: traderId,
          username: username ?? (row.username as string) ?? 'trader',
          maxContracts: (row.maxContracts as number | null) ?? null,
          maxPositions: (row.maxPositions as number | null) ?? null,
          traderRules: (row.traderRules as TraderRules | null) ?? null,
          openContracts: (row.openCount as number) ?? 0,
          openPositions: (row.openPositionsCount as number) ?? 0,
          intentContracts: (row.intentContracts as number) ?? 0,
        });
      } else {
        setLimits(null);
      }
    }

    if (balRes.ok && balRes.data) {
      const balData = balRes.data as Record<string, unknown>;
      setBalanceIsOrgPlaceholder(balData.scope === 'org');
      setBalance(balData);
    } else {
      setBalance(null);
      setBalanceIsOrgPlaceholder(false);
    }

    if (posRes.ok && posRes.data) {
      const posData = posRes.data as Record<string, unknown>;
      setPositions(
        toArr<RawPosition>(posData.market_positions ?? posData.positions ?? posData.results),
      );
    } else {
      setPositions([]);
    }

    if (ordRes.ok && ordRes.data) {
      const ordData = ordRes.data as Record<string, unknown>;
      setOrders(toArr<RawOrder>(ordData.orders ?? ordData.results));
    } else {
      setOrders([]);
    }

    if (fillRes.ok && fillRes.data) {
      const fillData = fillRes.data as Record<string, unknown>;
      setFills(toArr<RawFill>(fillData.fills ?? fillData.results));
    } else {
      setFills([]);
    }

    setPnl(pnlRes.ok ? (pnlRes.data as PnlSummary) : null);

    const anyOk = [balRes, posRes, ordRes, fillRes, pnlRes].some((r) => r.ok);
    if (!anyOk) setError('Could not load this trader’s activity.');
    setLoading(false);
  }, [traderId, orgQueryParam]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const realizedPnl = pnl?.realizedPnlCents ?? 0;
  const balanceCents = balanceIsOrgPlaceholder
    ? null
    : (balance?.balance ?? balance?.available_balance ?? null);

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-2 py-1 text-xs"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <Crosshair size={20} className="text-muted-foreground" />
            <h1 className="text-xl font-semibold">{username ?? 'Trader'}</h1>
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
        )}

        {/* Summary cards */}
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Balance</div>
            <div className="text-foreground mt-1 text-lg font-semibold">
              {balanceIsOrgPlaceholder
                ? <span className="text-muted-foreground text-xs font-normal">org-level</span>
                : balanceCents !== null
                  ? `$${(Number(balanceCents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                  : '—'}
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Realized P&L</div>
            <div className={`mt-1 text-lg font-semibold ${realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatDollars(realizedPnl)}
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Open Positions</div>
            <div className="text-foreground mt-1 text-lg font-semibold">{positions.length}</div>
          </div>
          <div className="bg-card border-border rounded-lg border px-4 py-3">
            <div className="text-muted-foreground text-xs">Open Orders</div>
            <div className="text-foreground mt-1 text-lg font-semibold">{orders.length}</div>
          </div>
          <GuardrailsTile
            limits={limits}
            canEdit={canEditLimits}
            onEdit={() => setEditingLimits(true)}
          />
        </div>

        {limits && (
          <EditLimitsModal
            isOpen={editingLimits}
            onClose={() => setEditingLimits(false)}
            trader={{
              id: limits.id,
              username: limits.username,
              maxContracts: limits.maxContracts,
              maxPositions: limits.maxPositions,
              traderRules: limits.traderRules,
            }}
            orgQueryParam={orgQueryParam}
            currentUsage={{
              openContracts: limits.openContracts,
              openPositions: limits.openPositions,
              intentContracts: limits.intentContracts,
            }}
            onSaved={(next) => {
              setLimits((prev) => prev ? { ...prev, ...next } : prev);
              // Re-fetch the rollup so the bars reflect the new caps + rules.
              fetchAll();
            }}
          />
        )}

        {/* Positions */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Positions</h2>
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
            </div>
          ) : positions.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              No open positions
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Avg</th>
                  <th className="px-3 py-2 text-right">Current</th>
                  <th className="px-3 py-2 text-right">Unrealized</th>
                  <th className="px-3 py-2 text-right">Realized</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => {
                  const ticker = p.ticker ?? p.market_ticker ?? '—';
                  const isV2 = typeof p.netCount === 'number';
                  const side: 'yes' | 'no' = isV2
                    ? (p.side ?? 'yes')
                    : (p.position ?? 0) >= 0
                      ? 'yes'
                      : 'no';
                  const qty = isV2 ? (p.netCount ?? 0) : Math.abs(p.position ?? p.quantity ?? 0);
                  const avg = isV2 ? (p.avgEntryPriceCents ?? 0) : Number(p.average_price ?? 0);
                  const yesPrice = getMarketPrice(ticker);
                  const currentPrice =
                    yesPrice == null ? null : side === 'yes' ? yesPrice : 100 - yesPrice;
                  // Both avg and currentPrice are in side-native space (the
                  // price for the side actually held). One uniform formula.
                  const unrealized =
                    currentPrice == null ? null : (currentPrice - avg) * qty;
                  const realized = typeof p.realizedPnlCents === 'number' ? p.realizedPnlCents : null;
                  const { marketLabel, betTeam } = describeMarket(ticker, side);
                  return (
                    <tr key={i} className="border-border/40 border-b" title={ticker}>
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{String(side).toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {qty}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {avg ? `${avg}¢` : '—'}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {currentPrice == null ? '—' : `${currentPrice}¢`}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          unrealized == null
                            ? 'text-muted-foreground'
                            : unrealized >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {unrealized == null ? '—' : formatDollars(unrealized)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          realized == null
                            ? 'text-muted-foreground'
                            : realized >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {realized == null ? '—' : formatDollars(realized)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Open Orders */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Open Orders</h2>
          </div>
          {orders.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">No open orders</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Bid</th>
                  <th className="px-3 py-2 text-right">Price</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => {
                  const ticker = o.ticker ?? o.market_ticker ?? '—';
                  // V2 single-book: a resting buy-NO reads back side:'yes'/
                  // action:'sell'/outcome_side:'no'. Reconstruct the trader-native
                  // side so the manager sees a NO book as NO (the /orders endpoint
                  // returns raw orders; this table never ran parseOrders). The
                  // bid/price derivation below already complements the leg.
                  const { side } = reconstructOrderTraderSide(o);
                  const qty =
                    pickNum(o.remaining_count, o.remaining_count_fp, o.remaining_quantity, o.count, o.count_fp, o.quantity) ?? 0;
                  // Side-specific bid first; fall back to the opposite-side
                  // price flipped (Kalshi often only emits yes_price even for
                  // NO orders, so a 58c NO arrives as yes_price=42).
                  const sideDollars = side === 'yes' ? o.yes_price_dollars : o.no_price_dollars;
                  const sideCents = side === 'yes' ? o.yes_price : o.no_price;
                  const oppositeDollars = side === 'yes' ? o.no_price_dollars : o.yes_price_dollars;
                  const oppositeCents = side === 'yes' ? o.no_price : o.yes_price;
                  const sideBid = dollarsToCents(sideDollars, sideCents);
                  const oppositeBid = dollarsToCents(oppositeDollars, oppositeCents);
                  const bid =
                    sideBid != null && sideBid > 0
                      ? sideBid
                      : oppositeBid != null && oppositeBid > 0
                        ? 100 - oppositeBid
                        : dollarsToCents(undefined, o.price);
                  const yesPrice = getMarketPrice(ticker);
                  const currentPrice =
                    yesPrice == null ? null : side === 'yes' ? yesPrice : 100 - yesPrice;
                  const { marketLabel, betTeam } = describeMarket(ticker, side);
                  return (
                    <tr
                      key={o.order_id ?? o.id ?? i}
                      className="border-border/40 border-b"
                      title={ticker}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{String(side).toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {qty}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {bid != null && bid > 0 ? `${bid}¢` : '—'}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {currentPrice == null ? '—' : `${currentPrice}¢`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent fills */}
        <div className="bg-card border-border rounded-lg border">
          <div className="border-border border-b px-4 py-2.5">
            <h2 className="text-foreground text-sm font-medium">Recent Fills</h2>
          </div>
          {fills.length === 0 ? (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">No recent fills</p>
          ) : (() => {
            const openKeys = new Set(
              positions.map((p) => {
                const ticker = p.ticker ?? p.market_ticker ?? '';
                const isV2 = typeof p.netCount === 'number';
                const side: 'yes' | 'no' = isV2
                  ? (p.side ?? 'yes')
                  : (p.position ?? 0) >= 0
                    ? 'yes'
                    : 'no';
                return `${ticker}|${side}`;
              }),
            );
            const fillPnl = annotateFillsWithPnl(
              fills,
              getMarketPrice,
              openKeys,
              pnl?.settlements,
            );
            return (
            <table className="w-full">
              <thead>
                <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Market</th>
                  <th className="px-3 py-2 text-center">Side</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Unrealized</th>
                  <th className="px-3 py-2 text-right">Realized</th>
                  <th className="px-3 py-2 text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {fills.slice(0, 30).map((f, i) => {
                  const ticker = f.ticker ?? f.market_ticker ?? '—';
                  const side: 'yes' | 'no' = f.side ?? 'yes';
                  const count = pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0;
                  const sideDollars = side === 'yes' ? f.yes_price_dollars : f.no_price_dollars;
                  const sideCents = side === 'yes' ? f.yes_price : f.no_price;
                  const price =
                    dollarsToCents(sideDollars ?? f.price_dollars, sideCents ?? f.price);
                  const when = f.created_time ?? f.created_at;
                  const { marketLabel, betTeam } = describeMarket(ticker, side);
                  const pnl = fillPnl.get(i) ?? { unrealizedCents: null, realizedCents: null };
                  return (
                    <tr
                      key={f.fill_id ?? f.trade_id ?? i}
                      className="border-border/40 border-b"
                      title={ticker}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        {marketLabel}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{String(side).toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {count}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {price != null && price > 0 ? `${price}¢` : '—'}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          pnl.unrealizedCents == null
                            ? 'text-muted-foreground'
                            : pnl.unrealizedCents >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {pnl.unrealizedCents == null ? '—' : formatDollars(pnl.unrealizedCents)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs font-medium ${
                          pnl.realizedCents == null
                            ? 'text-muted-foreground'
                            : pnl.realizedCents >= 0
                              ? 'text-green-400'
                              : 'text-red-400'
                        }`}
                      >
                        {pnl.realizedCents == null ? '—' : formatDollars(pnl.realizedCents)}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right text-[11px]">
                        {when ? new Date(when).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ── Guardrails tile ──────────────────────────────────────────────

/** Tight rule summary for the compact tile — mirrors Manager LimitsRow's
 *  summarizeTraderRules but truncates aggressively for the smaller surface. */
function summarizeRulesShort(rules: TraderRules | null): string | null {
  if (!rules) return null;
  const parts: string[] = [];
  if (rules.allowedSports !== null && rules.allowedSports.length > 0) {
    parts.push(rules.allowedSports.join('/') + ' only');
  }
  if (!rules.nonSportsEnabled) parts.push('no non-sport');
  if (!rules.polymarketEnabled) parts.push('no Poly');
  return parts.length > 0 ? parts.join(' · ') : null;
}

interface GuardrailsTileProps {
  limits: TraderLimitsSnapshot | null;
  canEdit: boolean;
  onEdit: () => void;
}

function GuardrailsTile({ limits, canEdit, onEdit }: GuardrailsTileProps) {
  if (!limits) {
    return (
      <div className="bg-card border-border rounded-lg border px-4 py-3">
        <div className="text-muted-foreground text-xs">Guardrails</div>
        <div className="text-muted-foreground mt-1 text-xs">—</div>
      </div>
    );
  }
  // Contracts cap includes resting buy intent so it mirrors what the relay
  // enforces on the next order placement.
  const contractsCurrent = limits.openContracts + limits.intentContracts;
  const cHead = computeHeadroom(contractsCurrent, limits.maxContracts);
  const pHead = computeHeadroom(limits.openPositions, limits.maxPositions);
  const statusColour: Record<string, string> = {
    green: 'text-emerald-400',
    yellow: 'text-amber-400',
    red: 'text-red-400',
    over: 'text-red-500',
    unlimited: 'text-blue-300',
  };
  const worst = [cHead.status, pHead.status].includes('over')
    ? 'over'
    : [cHead.status, pHead.status].includes('red')
      ? 'red'
      : [cHead.status, pHead.status].includes('yellow')
        ? 'yellow'
        : cHead.status === 'unlimited' && pHead.status === 'unlimited'
          ? 'unlimited'
          : 'green';

  return (
    <div className="bg-card border-border rounded-lg border px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-xs">Guardrails</div>
        {canEdit && (
          <button
            onClick={onEdit}
            title="Edit limits"
            className="text-muted-foreground hover:text-foreground rounded p-0.5"
          >
            <SettingsIcon size={11} />
          </button>
        )}
      </div>
      <div className={`mt-1 text-[11px] tabular-nums ${statusColour[worst]}`}>
        <div>Pos&nbsp;&nbsp; {formatHeadroomDisplay(limits.openPositions, limits.maxPositions)}</div>
        <div>Cont {formatHeadroomDisplay(contractsCurrent, limits.maxContracts)}</div>
        {summarizeRulesShort(limits.traderRules) && (
          <div
            className="mt-1 text-amber-400 truncate"
            title={summarizeRulesShort(limits.traderRules) ?? ''}
          >
            {summarizeRulesShort(limits.traderRules)}
          </div>
        )}
      </div>
    </div>
  );
}
